import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, AlertTriangle, Ban, CheckCircle, XCircle, Info } from 'lucide-react';

/**
 * SPONSOR POLICY PAGE — LOCKED DIRECTIVE
 * 
 * Public-facing legal language for sponsorship.
 * LikeKind does NOT run ads. Sponsorship is placement, not advertising.
 * 
 * Copy is verbatim from the locked directive.
 */

export default function SponsorPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-40" style={{ 
        backgroundColor: 'var(--fy-surface-card)', 
        borderBottom: '1px solid var(--fy-border-subtle)' 
      }}>
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center h-14">
            <button
              onClick={() => navigate(-1)}
              className="p-2 -ml-2 rounded-lg"
              style={{ color: 'var(--fy-text-secondary)' }}
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="ml-2 text-lg font-bold" style={{ color: 'var(--fy-text-primary)' }}>
              Sponsor Policy
            </h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Introduction */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
            What Sponsorship Is
          </h2>
          <p className="mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
            <strong style={{ color: 'var(--fy-text-primary)' }}>LikeKind sponsorship is placement, not advertising.</strong>
          </p>
          <p className="mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
            Sponsors may appear only within:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-1" style={{ color: 'var(--fy-text-secondary)' }}>
            <li>Approved category pages</li>
            <li>Approved Commons contexts</li>
            <li>Approved informational placements</li>
          </ul>
          
          <p className="mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
            Sponsorship does <strong style={{ color: 'var(--fy-text-primary)' }}>not</strong> include:
          </p>
          <ul className="list-disc pl-6 space-y-1" style={{ color: 'var(--fy-text-secondary)' }}>
            <li>Behavioral targeting</li>
            <li>Algorithmic amplification</li>
            <li>Impression optimization</li>
            <li>Engagement metrics</li>
            <li>Retargeting</li>
            <li>Data collection</li>
          </ul>
        </div>

        {/* Data Limits */}
        <div className="p-4 rounded-xl mb-8" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#7A8F5B' }} />
            <div>
              <p className="font-bold mb-2" style={{ color: 'var(--fy-text-primary)' }}>
                Sponsors receive zero user data.
              </p>
              <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                Sponsors do not receive analytics beyond: placement duration, placement category, and billing status.
              </p>
            </div>
          </div>
        </div>

        {/* What Sponsorship Is NOT */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
            What Sponsorship Is NOT
          </h2>
          <p className="mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
            Sponsors may <strong style={{ color: '#dc2626' }}>not</strong>:
          </p>
          <ul className="space-y-2" style={{ color: 'var(--fy-text-secondary)' }}>
            {[
              'Embed external scripts',
              'Link to unreviewed external content',
              'Change content after approval',
              'Use deceptive thumbnails or bait',
              'Circumvent review via redirects',
              'Promote illegal, explicit, or misleading material',
              'Target minors or restricted audiences',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#dc2626' }} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Data & Privacy */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
            Data & Privacy
          </h2>
          <p className="mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
            LikeKind:
          </p>
          <ul className="space-y-2 mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
            {[
              'Does not sell data',
              'Does not track user behavior',
              'Does not share user identity',
              'Does not permit sponsor tracking pixels',
              'Does not allow external embeds',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A8F5B' }} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Enforcement */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
            Sponsor Violation & Enforcement
          </h2>
          <p className="mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
            All violations incur financial loss. Mistakes cost money — intentional or accidental.
          </p>
          
          {/* Stage 1 */}
          <div className="p-4 rounded-xl mb-3" style={{ backgroundColor: '#fef3c7', border: '1px solid #f59e0b' }}>
            <h3 className="font-bold mb-2" style={{ color: '#92400e' }}>
              Stage 1 — Automatic Removal
            </h3>
            <ul className="text-sm space-y-1" style={{ color: '#92400e' }}>
              <li>• Content removed immediately</li>
              <li>• No refund</li>
              <li>• Warning logged</li>
              <li>• Sponsor notified</li>
            </ul>
          </div>

          {/* Stage 2 */}
          <div className="p-4 rounded-xl mb-3" style={{ backgroundColor: '#fed7aa', border: '1px solid #ea580c' }}>
            <h3 className="font-bold mb-2" style={{ color: '#9a3412' }}>
              Stage 2 — Suspension
            </h3>
            <ul className="text-sm space-y-1" style={{ color: '#9a3412' }}>
              <li>• Sponsorship privileges suspended</li>
              <li>• Forfeiture of remaining placement time</li>
              <li>• Re-review required</li>
              <li>• Administrative flag added</li>
            </ul>
          </div>

          {/* Stage 3 */}
          <div className="p-4 rounded-xl mb-4" style={{ backgroundColor: '#fecaca', border: '1px solid #dc2626' }}>
            <h3 className="font-bold mb-2" style={{ color: '#991b1b' }}>
              Stage 3 — Permanent Ban
            </h3>
            <ul className="text-sm space-y-1" style={{ color: '#991b1b' }}>
              <li>• Account banned</li>
              <li>• Payment forfeited</li>
              <li>• Future placements prohibited</li>
              <li>• Record retained for audit</li>
            </ul>
          </div>

          {/* Child Safety Override */}
          <div className="p-4 rounded-xl" style={{ backgroundColor: '#fecaca', border: '2px solid #dc2626' }}>
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#dc2626' }} />
              <div>
                <h3 className="font-bold mb-1" style={{ color: '#991b1b' }}>
                  Child & Family Safety Override
                </h3>
                <p className="text-sm" style={{ color: '#991b1b' }}>
                  If a placement risks exposure to minors, links to mixed-audience content, or can change post-approval → 
                  <strong> Immediate Stage 3. No exceptions. No appeals.</strong>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-4">
            <div>
              <h3 className="font-bold mb-1" style={{ color: 'var(--fy-text-primary)' }}>
                Q: Are these ads?
              </h3>
              <p style={{ color: 'var(--fy-text-secondary)' }}>
                No. LikeKind does not run ads. Sponsors appear only by placement, not targeting.
              </p>
            </div>
            
            <div>
              <h3 className="font-bold mb-1" style={{ color: 'var(--fy-text-primary)' }}>
                Q: Do sponsors see my data?
              </h3>
              <p style={{ color: 'var(--fy-text-secondary)' }}>
                No. Sponsors receive zero user data.
              </p>
            </div>
            
            <div>
              <h3 className="font-bold mb-1" style={{ color: 'var(--fy-text-primary)' }}>
                Q: Why are violations punished financially?
              </h3>
              <p style={{ color: 'var(--fy-text-secondary)' }}>
                Because trust requires accountability. Mistakes must carry consequences.
              </p>
            </div>
            
            <div>
              <h3 className="font-bold mb-1" style={{ color: 'var(--fy-text-primary)' }}>
                Q: Can sponsors change content later?
              </h3>
              <p style={{ color: 'var(--fy-text-secondary)' }}>
                No. Any change requires re-review and re-approval.
              </p>
            </div>
          </div>
        </div>

        {/* Why Am I Seeing This */}
        <div className="p-4 rounded-xl" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--fy-text-secondary)' }} />
            <div>
              <h3 className="font-bold mb-1" style={{ color: 'var(--fy-text-primary)' }}>
                Why Am I Seeing This?
              </h3>
              <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                When you see a sponsor placement on LikeKind, it means:
              </p>
              <ul className="text-sm mt-2 space-y-1" style={{ color: 'var(--fy-text-secondary)' }}>
                <li>• This placement supports LikeKind</li>
                <li>• No data is shared with the sponsor</li>
                <li>• No tracking or targeting is involved</li>
                <li>• The content has been manually reviewed and approved</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Related Policies */}
        <div className="mt-8 p-4 rounded-xl" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
          <h3 className="font-bold mb-3" style={{ color: 'var(--fy-text-primary)' }}>
            Related Policies
          </h3>
          <div className="space-y-2">
            <button
              onClick={() => navigate('/content-guidelines')}
              className="w-full text-left p-3 rounded-lg transition-colors hover:bg-white/50"
              style={{ color: 'var(--fy-text-secondary)' }}
            >
              Content Guidelines →
            </button>
            <button
              onClick={() => navigate('/group-policy')}
              className="w-full text-left p-3 rounded-lg transition-colors hover:bg-white/50"
              style={{ color: 'var(--fy-text-secondary)' }}
            >
              Group Policy →
            </button>
          </div>
        </div>

        {/* Last Updated */}
        <p className="text-center text-xs mt-8" style={{ color: 'var(--fy-text-disabled)' }}>
          Last updated: January 2026
        </p>
      </main>
    </div>
  );
}
